import { request } from '../utils/request';
import type {
  CreateRouteSurveyRequest,
  CreateRouteSurveyResponse,
  RouteSurveyResponse,
} from '../types/route-survey.types';
import { submitRouteSurveyOffline } from '../services/offline-surveys.service';

export const createRouteSurvey = async (
  data: CreateRouteSurveyRequest,
): Promise<CreateRouteSurveyResponse> => {
  // If offline, save to IndexedDB
  if (!navigator.onLine) {
    const localId = await submitRouteSurveyOffline(data);
    return {
      message:
        'Survey saved offline. It will be submitted when you are back online.',
      uuid: localId, // Return local ID as UUID for now
    };
  }

  // Online: submit to server
  return request<CreateRouteSurveyResponse>('/surveys/route-surveys/', {
    method: 'POST',
    body: data,
  });
};

export const getRouteSurveyByUuid = async (
  uuid: string,
): Promise<RouteSurveyResponse> => {
  return request<RouteSurveyResponse>(`/surveys/route-surveys/${uuid}`);
};

export const markRouteSurveyAsViewed = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request<{ message: string }>(`/surveys/route-surveys/${uuid}/viewed`, {
    method: 'PATCH',
  });
};

export const markRouteSurveyAsUnviewed = async (
  uuid: string,
): Promise<{ message: string }> => {
  return request<{ message: string }>(
    `/surveys/route-surveys/${uuid}/unviewed`,
    {
      method: 'PATCH',
    },
  );
};

export const downloadRouteSurveyPdf = async (uuid: string): Promise<void> => {
  const { blob, filename } = await request<{
    blob: Blob;
    filename: string | null;
  }>(`/surveys/route-surveys/${uuid}/download`, {
    responseType: 'blob',
    returnFilename: true,
  });

  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? 'RouteSurvey.pdf';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
